import { useEffect, useState } from 'preact/hooks';

import { setQueryParams } from '@/hooks/route';

import { parseResumeState, RESUME_PARAM } from '../core/resumeState';
import { setPendingResume } from '../pendingResume';

/**
 * The whole life of the `sf` parameter: read it, hand it on, and spend it.
 *
 * It restores a choice made before the visitor was sent away — to a magic link, to a verification
 * email, or to Stripe in this same tab when a popup was blocked.
 *
 * **Read during render**, because it is offered to the gate the page declares rather than opened as
 * a flow of its own, and every effect on the page runs after every render — an effect here would be
 * too late for a gate that fires on mount. Opening a flow here instead meant racing that gate, and
 * losing in a way that looked like winning: it ran first, but on a fresh page load it ran before the
 * session had resolved, so the visitor read as signed out and got the product list they had just
 * been through instead of the payment step.
 *
 * **Removed from the URL afterwards**, read or not. It is an instruction — "they had already picked
 * this" — not a description of the page, and left in place it outlives its one use: a reload
 * resumes a purchase abandoned days ago, a bookmark keeps resuming it, and a link shared with a
 * friend pushes *them* towards paying for something they never chose. Harmless by ADR 0006, which
 * is why it carries ids and never pays by itself, but not something to hand out. It can still bite
 * after the purchase goes through, too: an explicit `ppt` opens a flow for a satisfied gate on
 * purpose, and this would seed that flow with the product they have just bought.
 *
 * Spent here rather than on completion because most journeys never reach completion — they dismiss,
 * navigate away, or abandon the checkout, and every one of those would leave it behind. Replaced
 * rather than pushed: this is not somewhere the visitor can go back to. The same-tab Stripe fallback
 * is unaffected; it writes a fresh `sf` from the current URL at the moment it navigates.
 */
const useResumeState = () => {
  useState(() => setPendingResume(parseResumeState(new URLSearchParams(window.location.search).get(RESUME_PARAM))));

  useEffect(() => {
    if (new URLSearchParams(window.location.search).has(RESUME_PARAM)) {
      setQueryParams({ [RESUME_PARAM]: undefined }, true);
    }
  }, []);
};

export default useResumeState;
